<template>
    <v-card class="tw-rounded-xl tw-shadow-md tw-bg-white">
        <v-card-title>
            <div
                class="tw-rounded-md tw-inline-block tw-w-full tw-text-sm tw-p-3"
                :style="{
                    backgroundColor: `${studio.primaryColor}2e`,
                    color: studio.webAppFontColor,
                }"
            >
                {{ packageItem.name }}
            </div>
        </v-card-title>

        <v-card-text>
            <div class="tw-flex tw-items-center tw-mb-1">
                <Avatar
                    :size="32"
                    class="tw-ml-2 tw-mr-4"
                    :fullname="packageItem.fullName"
                    :color="getColour(packageItem.fullName)"
                />
                <div class="tw-flex-1">
                    <div class="tw-text-gray-500 night:tw-text-gray-200">
                        {{ $t('message.packageOwner') }} :
                    </div>
                    <div class="tw-font-bold">
                        {{ packageItem.fullName }}
                    </div>
                </div>
                <div
                    class="tw-text-right tw-font-bold tw-text-black night:tw-text-gray-100"
                >
                    <span
                        :class="
                            packageItem.classesLeft > 0 ? 'tw-text-success' : ''
                        "
                        >{{ packageItem.classesLeft }}</span
                    >
                    <span>
                        / {{ packageItem.numberClasses }}
                        {{ $t('message.classesLeft') }}</span
                    >
                </div>
            </div>

            <v-divider class="tw-my-2"></v-divider>

            <div class="tw-grid tw-grid-cols-2 tw-gap-y-2 tw-mt-4">
                <div>
                    <div>{{ $t('message.packageValue') }} :</div>
                    <div class="tw-font-bold">
                        {{ this.studio.currency }}{{ packageItem.price }}
                    </div>
                </div>
                <div>
                    <div>{{ $t('message.groupBooking') }}</div>
                    <div class="tw-font-bold">
                        <span
                            v-if="packageItem.isGroupBookable"
                            class="tw-text-success"
                            >{{ $t('message.eligible') }}</span
                        >
                        <span v-else>{{ $t('message.notEligible') }}</span>
                    </div>
                </div>
                <div>
                    <div>{{ $t('message.purchasedOn') }}</div>
                    <div class="tw-font-bold">
                        {{
                            dateStringFromTimestamp(
                                packageItem.datePurchased,
                                studio.timezone
                            )
                        }}
                    </div>
                </div>
                <div>
                    <div>{{ $t('message.expiresOn') }}</div>
                    <div class="tw-font-bold tw-text-error">
                        {{
                            dateStringFromTimestamp(
                                packageItem.expiresOn,
                                studio.timezone
                            )
                        }}
                    </div>
                </div>
            </div>

            <!-- payment method -->
            <div v-if="packageItem.paymentMethodId">
                <v-skeleton-loader
                    type="list-item"
                    class="tw-rounded-md"
                    v-if="paymentMethodLoading"
                />
                <div
                    class="tw-my-4 tw-px-2 tw-bg-green-100 tw-rounded night:tw-bg-green-900 night:tw-text-white"
                    v-else-if="paymentMethods[packageItem.paymentMethodId]"
                >
                    <PaymentMethodItemDetails
                        :paymentMethod="
                            paymentMethods[packageItem.paymentMethodId]
                        "
                    >
                        <template #actions>
                            <v-list-item-action class="tw-mr-2">
                                <v-btn
                                    icon
                                    @click="showPaymentMethodsDialog = true"
                                >
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </template>
                    </PaymentMethodItemDetails>
                </div>
            </div>
            <BaseDialog
                :value="showPaymentMethodsDialog"
                :onClose="closePaymentMethodsDialog"
                @input="closePaymentMethodsDialog"
                :hideHeader="true"
                :defaultPadding="false"
                :width="600"
            >
                <PaymentMethods :packageId="packageItem.id" />
            </BaseDialog>

            <div class="tw-flex tw-justify-end tw-mt-4">
                <!-- view more button -->
                <PackageInfo
                    :buttonText="$t('message.viewDetails')"
                    :packageItem="packageItem"
                    :isBuyable="false"
                    @sharePackage="sharePackage"
                />

                <!-- cancel package button -->
                <PackageCancelButton
                    v-if="isRecurringPackageUserCancellable"
                    :packageId="packageItem.id"
                    @cancelStatus="cancelStatus"
                />

                <!-- assign package button -->
                <!-- hide the assign package button as it leads to confusion -->
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
import Avatar from 'vue-avatar-component'
import { mapGetters } from 'vuex'
import PackageInfo from '@/components/packages/PackageInfo'
import BaseDialog from '@/components/base/dialog/BaseDialog.vue'
import PackageCancelButton from '@/components/packages/PackageCancelButton.vue'
import PaymentMethodItemDetails from '@/components/paymentmethods/PaymentMethodItemDetails.vue'
import { useToast } from '@/components/base/toast/composable/useToast'
import PaymentMethods from '@/views/PaymentMethods'
import {
    STUDIO_MODULE_NAME,
    AUTHENTICATION_MODULE_NAME,
} from '@/store/moduleNames'
import { dateStringFromTimestamp } from '@/util/dateformat'
import { listStudioMemberPaymentMethods } from '@/util/cloudFunctions/paymentMethods'
import { getColour } from '@/util/profile'

export default {
    components: {
        Avatar,
        PackageInfo,
        PaymentMethods,
        BaseDialog,
        PaymentMethodItemDetails,
        PackageCancelButton,
    },
    props: {
        packageItem: {
            type: Object,
            required: true,
        },
        subAccounts: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            assignPackageDialog: false,
            showPaymentMethodsDialog: false,
            paymentMethods: {},
            paymentMethodLoading: false,
        }
    },
    setup() {
        const { showErrorToast } = useToast()
        return {
            showErrorToast,
        }
    },
    computed: {
        ...mapGetters({
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
            user: `${AUTHENTICATION_MODULE_NAME}/getUser`,
        }),
        hasSubAccounts() {
            return this.subAccounts.length > 0
        },
        isAssignable() {
            return (
                this.hasSubAccounts && this.packageItem.userId === this.user.id
            )
        },
        isRecurringPackage() {
            return this.packageItem.isSubscription
        },
        isRecurringPackageUserCancellable() {
            return (
                this.isRecurringPackage &&
                this.packageItem.isUserCancellable &&
                this.packageItem.status === 'active'
            )
        },
        showChangePaymentMethodBtn() {
            return (
                this.isRecurringPackage &&
                !this.isBuyable &&
                this.packageItem.status === 'active'
            )
        },
    },
    created() {
        this.listPaymentMethods()
    },
    methods: {
        getColour,
        sharePackage() {
            this.$emit('sharePackage')
        },
        dateStringFromTimestamp(timestamp, timezone) {
            return dateStringFromTimestamp(
                timestamp,
                timezone,
                'ddd, D MMM YYYY'
            )
        },
        openAssignPackageDialog() {
            this.assignPackageDialog = true
        },
        refresh() {
            this.$emit('refresh')
        },
        closePaymentMethodsDialog() {
            this.showPaymentMethodsDialog = false
        },
        async listPaymentMethods() {
            this.paymentMethodLoading = true
            try {
                const paymentMethodsMapping = {}
                const result = await listStudioMemberPaymentMethods(
                    this.studio.id
                )
                if (result.success) {
                    const {
                        paymentMethods,
                        mainAccountPaymentMethods,
                        subAccountPaymentMethods,
                    } = result
                    if (paymentMethods && paymentMethods.length) {
                        paymentMethods.forEach((paymentMethod) => {
                            paymentMethodsMapping[paymentMethod.id] =
                                paymentMethod
                        })
                    }
                    if (
                        mainAccountPaymentMethods &&
                        mainAccountPaymentMethods.length
                    ) {
                        mainAccountPaymentMethods.forEach((paymentMethod) => {
                            paymentMethodsMapping[paymentMethod.id] =
                                paymentMethod
                        })
                    }
                    if (
                        subAccountPaymentMethods &&
                        subAccountPaymentMethods.length
                    ) {
                        subAccountPaymentMethods.forEach((paymentMethod) => {
                            paymentMethodsMapping[paymentMethod.id] =
                                paymentMethod
                        })
                    }
                    this.paymentMethods = paymentMethodsMapping
                } else {
                    throw result.error
                }
            } catch (error) {
                showErrorToast(error)
            } finally {
                this.paymentMethodLoading = false
            }
        },
    },
}
</script>
